import React, { useState, useEffect } from "react";
import { userInsuranceServiceList, expenseReimbursement25K, expenseReimbursement1M, expenseReimbursement2M } from "../../config/UserServicesList";
import { useTranslation } from "react-i18next";

interface IProps {
    userServices: any;
    userState: string;
    userCountry: string;
    isNYStateUser: boolean;
    isIDFPartner: boolean;
    isAvastId: boolean;
    isAVGId: boolean;
    isAVGTestId: boolean;
    isInsuranceLoading: boolean;
    insuranceMembership: any;
    currentLanguage: string;
}

interface ServiceObject {
    serviceCode: string;
    serviceDescription: string;
    dollarAmount: string;
    insuranceDocument: string;
    aigDocument: string;
    swissReUSDocument: string;
    swissReCADocument: string;
    swissReCAFRDocument: string;
}

const InsuranceIDF = ({
    userServices,
    userState,
    userCountry,
    isNYStateUser,
    isIDFPartner,
    isAvastId,
    isAVGId,
    isAVGTestId,
    isInsuranceLoading,
    insuranceMembership,
    currentLanguage,
}: IProps) => {
    const { t: tLang } = useTranslation();

    const [activeServices, setActiveServices] = useState<ServiceObject[]>([]);
    const [packageType, setPackageType] = useState("");
    const [insuranceDocument, setInsuranceDocument] = useState("");
    const [insuranceAmount, setInsuranceAmount] = useState("");

    const lloyds = ["eri25k", "eri1m", "eri2m"];
    const endorsements = ["SVC-RNSM-REIMB", "SVC-SOCENG-REIMB", "SVC-CB-25K", "SVC-CB-50K"];

    // Define service objects to be manually inserted into the array of active services based on addonCount
    // this enables us to reuse the same pdf file paths used within EZS expense reimbursement services for IDF
    const rnsm = {
        serviceId: 140,
        serviceCode: "SVC-RNSM-REIMB",
        displayName: "Ransomware Reimbursement",
        active: true,
        availableForPurchase: false,
        serviceFeatures: null,
        serviceCategory: "Recover",
        serviceCategoryId: 0,
        lastScanDate: null,
        displayText: null,
        lastUpdateDate: null,
        serviceName: "Ransomware Reimbursement",
        sortOrder: null,
        productDescription:
            "Expense reimbursement insurance to pay covered costs such as lawyer fees, loss of wages and more associated with the resolution process.",
        partnerPaid: false,
    };

    const soceng = {
        serviceId: 141,
        serviceCode: "SVC-SOCENG-REIMB",
        displayName: "Social Engineering Reimbursement",
        active: true,
        availableForPurchase: false,
        serviceFeatures: null,
        serviceCategory: "Recover",
        serviceCategoryId: 0,
        lastScanDate: null,
        displayText: null,
        lastUpdateDate: null,
        serviceName: "Social Engineering Reimbursement",
        sortOrder: null,
        productDescription:
            "Expense reimbursement insurance to pay covered costs such as lawyer fees, loss of wages and more associated with the resolution process.",
        partnerPaid: false,
    };

    const cb25k = {
        serviceId: 1189,
        serviceCode: "SVC-CB-25K",
        displayName: "Cyberbullying Reimbursement",
        active: true,
        availableForPurchase: false,
        serviceFeatures: null,
        serviceCategory: "Recover",
        serviceCategoryId: 0,
        lastScanDate: null,
        displayText: null,
        lastUpdateDate: null,
        serviceName: "$25K Cyber Bullying",
        sortOrder: null,
        productDescription: "Extend online identity theft protection services to a family member.",
        partnerPaid: false,
    };

    const cb50k = {
        serviceId: 1190,
        serviceCode: "SVC-CB-50K",
        displayName: "Cyberbullying Reimbursement",
        active: true,
        availableForPurchase: false,
        serviceFeatures: null,
        serviceCategory: "Recover",
        serviceCategoryId: 0,
        lastScanDate: null,
        displayText: null,
        lastUpdateDate: null,
        serviceName: "$50K Cyber Bullying",
        sortOrder: null,
        productDescription: "Extend online identity theft protection services to a family member.",
        partnerPaid: false,
    };

    const addonServiceMap: Record<number, null | any | any[]> = {
        0: null,
        1: [rnsm],
        2: [rnsm, soceng],
        3: [rnsm, soceng, cb25k],
        4: [rnsm, soceng, cb50k],
    };

    // userServices = [
    //     {
    //         serviceId: 0,
    //         serviceCode: "eri1m",
    //         displayName: "Expense Reimbursement Insurance $1M",
    //         active: true,
    //         availableForPurchase: false,
    //         serviceFeatures: [{ key: "addonCount", value: 2 }],
    //         serviceCategory: null,
    //         serviceCategoryId: 0,
    //         lastScanDate: null,
    //         displayText: null,
    //         lastUpdateDate: null,
    //         serviceName: null,
    //         sortOrder: null,
    //         productDescription: null,
    //         partnerPaid: false,
    //     },
    // ];

    const getActiveInsuranceServices = () => {
        if (userServices && userServices.length > 0) {
            let insuranceServices: {
                serviceCode: string;
                serviceDescription: string;
                dollarAmount: string;
                insuranceDocument: string;
                aigDocument: string;
                swissReUSDocument: string;
                swissReCADocument: string;
                swissReCAFRDocument: string;
            }[] = [];

            let updatedServices = [...userServices];

            userServices.forEach((service: any) => {
                const addonFeature = service?.serviceFeatures?.find((feature: any) => feature.key === "addonCount");
                const addonValue = Number(addonFeature?.value);

                if (typeof addonValue === "number" && addonValue !== 0) {
                    const extraServices = addonServiceMap[addonValue];

                    extraServices?.forEach((addonService: any) => {
                        const alreadyExists = updatedServices.some((s) => s.serviceCode.toLowerCase() === addonService.serviceCode.toLowerCase());

                        if (!alreadyExists) {
                            updatedServices.push(addonService);
                        }
                    });
                }
            });

            updatedServices.forEach((service: any) => {
                const matches = userInsuranceServiceList.filter(
                    (insurance) => insurance.serviceCode.toLowerCase() === service.serviceCode.toLowerCase() && service.active
                );

                matches.forEach((matchedService) => {
                    if (!insuranceServices.some((s) => s.serviceCode === matchedService.serviceCode)) {
                        insuranceServices = [...insuranceServices, matchedService];
                    }
                });
            });

            if (insuranceServices.length > 0) {
                setActiveServices(insuranceServices);
            }
        }
    };

    const getInsurancePackageDetails = () => {
        const matches = activeServices.filter((service) => lloyds.includes(service.serviceCode));
        const matchCount = {
            lloyds: matches.filter((service) => lloyds.includes(service.serviceCode)).length,
            endorsements: activeServices.filter((service) => endorsements.includes(service.serviceCode)).length,
        };

        if (matchCount.lloyds > 0) {
            if (matchCount.endorsements > 0) {
                setPackageType("combinationEndorsement");
            } else {
                setPackageType("Lloyds");
            }
        }
    };

    const getProviderByRegion = () => {
        if ((userState === "NY" && userCountry === "US") || isNYStateUser) {
            getDocumentByProvider("AIG");
        } else if (userCountry === "US" || !userCountry) {
            getDocumentByProvider("SwissReUS");
        } else if (userCountry === "CA") {
            getDocumentByProvider("SwissReCA");
        } else {
            getDocumentByProvider("InternationalLloyds");
        }
    };

    const getServiceObject = (provider: string) => {
        let filteredService = activeServices.filter((service) => {
            if (activeServices.length > 1) {
                if (packageType === "combinationEndorsement") {
                    return lloyds.includes(service.serviceCode);
                } else {
                    if (provider === "Lloyds" || provider === "SwissReUS" || provider === "SwissReCA" || provider === "InternationalLloyds") {
                        return lloyds.includes(service.serviceCode);
                    } else {
                        return true;
                    }
                }
            } else {
                return true;
            }
        });

        // for endorsement packages, pull document from endorsement specific object based on EXPINS variation
        if (packageType === "combinationEndorsement") {
            const priorityOrder = ["SVC-CB-50K", "SVC-CB-25K", "SVC-SOCENG-REIMB", "SVC-RNSM-REIMB"];
            const endorsementListByPriority = activeServices
                .filter((service) => endorsements.includes(service.serviceCode))
                .sort((a, b) => {
                    return priorityOrder.indexOf(a.serviceCode) - priorityOrder.indexOf(b.serviceCode);
                });

            if (filteredService.length === 1 && endorsementListByPriority.length > 0) {
                // Get the highest priority endorsement
                const highestPriorityEndorsement = endorsementListByPriority[0].serviceCode;

                // Check if we have the specific combination of SVC-SOCENG-REIMB and SVC-RNSM-REIMB as the highest priorities
                // This will only be true if no CB variants are present (since they have higher priority)
                const hasSocialEngineering = highestPriorityEndorsement === "SVC-SOCENG-REIMB";
                const hasRansomware = endorsementListByPriority.length > 1 && endorsementListByPriority[1].serviceCode === "SVC-RNSM-REIMB";
                const useSpecialCombination = hasSocialEngineering && hasRansomware;

                // Determine which service code to use for lookup
                const lookupServiceCode = useSpecialCombination ? "SVC-SOCENG-REIMB+SVC-RNSM-REIMB" : highestPriorityEndorsement;

                if (filteredService[0].serviceCode.toLowerCase() === "eri25k") {
                    const matchedObject = expenseReimbursement25K.find((item) => item.serviceCode === lookupServiceCode);
                    filteredService = matchedObject ? [matchedObject] : filteredService;
                } else if (filteredService[0].serviceCode.toLowerCase() === "eri1m") {
                    const matchedObject = expenseReimbursement1M.find((item) => item.serviceCode === lookupServiceCode);
                    filteredService = matchedObject ? [matchedObject] : filteredService;
                } else if (filteredService[0].serviceCode.toLowerCase() === "eri2m") {
                    const matchedObject = expenseReimbursement2M.find((item) => item.serviceCode === lookupServiceCode);
                    filteredService = matchedObject ? [matchedObject] : filteredService;
                }
            }
        }

        return filteredService[0];
    };

    const getDocumentByProvider = (provider: string) => {
        // selects which service object to use when fetching document path
        const selectedService = getServiceObject(provider);
        let insuranceDocument = "";

        if (provider === "AIG") {
            insuranceDocument = selectedService.aigDocument ? selectedService.aigDocument : selectedService.insuranceDocument;
        } else if (provider === "SwissReUS") {
            insuranceDocument = selectedService.swissReUSDocument;
        } else if (provider === "SwissReCA") {
            insuranceDocument = currentLanguage == "fr" ? selectedService.swissReCAFRDocument : selectedService.swissReCADocument;
        } else {
            insuranceDocument = selectedService.swissReUSDocument ? selectedService.swissReUSDocument : selectedService.insuranceDocument;
        }

        setInsuranceAmount(selectedService.dollarAmount);
        setInsuranceDocument(insuranceDocument);
    };

    useEffect(() => {
        getActiveInsuranceServices();
    }, []);

    useEffect(() => {
        if (activeServices.length > 0) {
            getInsurancePackageDetails();
        }
    }, [activeServices]);

    useEffect(() => {
        if (packageType) {
            getProviderByRegion();
        }
    }, [packageType]);

    return (
        <div className={isIDFPartner ? "box box-border-top" : "box"}>
            <div className="box-header">
                <div className="box-title-container">
                    <div className="box-title-text">
                        <h2 className="box-title" id="credit-score-trend-title">
                            {isAvastId || isAVGId || isAVGTestId ? "Expense Reimbursement " : tLang("supportSection.insuranceTitleText")}
                        </h2>
                    </div>
                </div>
                <div className="box-description"></div>
                <div className="box-actions"></div>
            </div>
            {isInsuranceLoading ? (
                <div className="box-content loading"></div>
            ) : (
                <div className="box-content">
                    <p>
                        {tLang("supportSection.generalSupportWidget.insuranceContent.message.initialText")} {insuranceAmount} {insuranceMembership}{" "}
                        {tLang("supportSection.generalSupportWidget.insuranceContent.message.middleText")}
                        {isAvastId || isAVGId || isAVGTestId ? "reimbursement " : " insurance"}{" "}
                        {tLang("supportSection.generalSupportWidget.insuranceContent.message.lastText")}
                    </p>

                    <p id="footnote-label">
                        <small id="footnotes">
                            {tLang("supportSection.generalSupportWidget.insuranceContent.notification.initialText")}{" "}
                            {isAvastId || isAVGId || isAVGTestId ? "reimbursement " : "insurance"}{" "}
                            {tLang("supportSection.generalSupportWidget.insuranceContent.notification.lastText")}
                        </small>
                    </p>

                    <p>
                        {tLang("supportSection.generalSupportWidget.insuranceContent.link.content")}{" "}
                        <a type="button" target="_blank" className="default_link" href={"doc/" + insuranceDocument} rel="noreferrer">
                            <span className="btn-text">{tLang("supportSection.generalSupportWidget.insuranceContent.link.buttonText")}</span>
                        </a>
                    </p>
                </div>
            )}
        </div>
    );
};

export default InsuranceIDF;
